import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DIRECTION_BEARINGS, WIND_SECTORS } from '../lib/courseConstants.js';
import { parseMarkSequence, hasMarkSeparator, endsWithMarkSeparator, splitMarkTokens } from '../lib/courseParser.js';
import { inferRoundings, calculateDistance, calculateFullDistance, fuzzyMatchMark } from '../lib/roundingInference.js';
import { bearingDeg, offsetLineBearing, fixedMarkLineBearing } from '../lib/geo.js';
import { canonicalizeMarkIds } from '../lib/coursePreviewLayout.js';

/**
 * CourseEditor — single course editor for a direction+number slot.
 *
 * Features:
 * - Mark sequence input (uppercase letters)
 * - Port/starboard auto-inferred from mark positions
 * - User can override any mark's rounding
 * - Variation pre-populate (D5 from D4)
 * - Missing mark inline resolution
 * - Prev/next variation navigation
 * - Auto-calculated distance
 */
export default function CourseEditor({
  directionCode,
  courseNumber,
  course,
  adjacentCourse,
  marks,
  markLookup,
  onChange,
  onNavigate,
  onAddMark,
  onRemoveCourse,
  onRenameCourse,
  onWindDirectionChange,
  onStartAreaChange,
  resolvedCardLine,
  courseName,
  windDirection,
  startArea,
  prevNumber,
  nextNumber,
  children,
}) {
  // Prev/next course numbers within the configured grid row. Fall back to the
  // DBSC contiguous 1..8 assumption when the caller doesn't supply them, so a
  // gapped or narrower grid navigates to real cells instead of A10 / A5. The
  // `=== undefined` guard lets a caller pass `null` to mean "no neighbour".
  const prevNum = prevNumber !== undefined ? prevNumber : (courseNumber > 1 ? courseNumber - 1 : null);
  const nextNum = nextNumber !== undefined ? nextNumber : (courseNumber < 8 ? courseNumber + 1 : null);
  const [markInput, setMarkInput] = useState('');
  const [manualOverrides, setManualOverrides] = useState({});
  const [addingMark, setAddingMark] = useState(null); // markId being added
  const [highlightedLeg, setHighlightedLeg] = useState(null);
  const [stableId] = useState(() => course?.id || crypto.randomUUID());
  const prevCellRef = useRef(null);
  const savedDirectionsRef = useRef(null);

  const isNamedCourse = directionCode == null;
  const displayName = isNamedCourse ? (courseName || 'Custom') : `${directionCode}${courseNumber}`;

  // Sync markInput from course on cell switch only (not on feedback from onChange)
  useEffect(() => {
    if (course && course.markIds && course.markIds.length > 0) {
      // Use space-separated for multi-char mark IDs
      const hasMultiChar = course.markIds.some((id) => id.length > 1);
      const input = course.markIds.map((id) => id.toUpperCase()).join(hasMultiChar ? ' ' : '');
      setMarkInput(input);
      // Restore manual overrides from saved markDirections that differ from inference
      const saved = course.markDirections || [];
      setManualOverrides({});
      // Store saved directions, with the mark input they belong to, to reconcile
      // once inference runs for THAT input (see the reconcile effect below).
      savedDirectionsRef.current = { input, dirs: saved };
    } else {
      // A blank cell starts EMPTY — copying the neighbour's marks is now an
      // explicit "copy from neighbour" action, not automatic. (Chris, ra-npo7)
      setMarkInput('');
      setManualOverrides({});
      savedDirectionsRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionCode, courseNumber, course?.id]);

  // Fill the blank cell from the adjacent (neighbour) course on demand.
  const copyFromNeighbour = useCallback(() => {
    if (!adjacentCourse?.markIds?.length) return;
    const hasMultiChar = adjacentCourse.markIds.some((id) => id.length > 1);
    setMarkInput(adjacentCourse.markIds.map((id) => id.toUpperCase()).join(hasMultiChar ? ' ' : ''));
    setManualOverrides({});
    savedDirectionsRef.current = null;
  }, [adjacentCourse]);

  // Parse marks from input
  const parsedMarks = useMemo(
    () => parseMarkSequence(markInput, 'port'),
    [markInput],
  );

  const markIds = useMemo(
    () => parsedMarks.map((m) => m.id),
    [parsedMarks],
  );

  // markInput is always displayed/parsed uppercase, but marks are keyed by their
  // real id (uppercase club marks, lowercase MarksManager marks). Fold each id to
  // the matching mark's actual case ONCE and use it everywhere — inference,
  // distance, and persistence — so a lowercase MarksManager mark ('mug') resolves
  // its markLookup entry instead of saving default roundings + distance 0, and the
  // persisted markIds match what was used to compute them. (ra-or1y, codex review.)
  const canonicalMarkIds = useMemo(
    () => canonicalizeMarkIds(markIds, marks),
    [markIds, marks],
  );

  // Auto-infer roundings
  const inferred = useMemo(
    () => inferRoundings(canonicalMarkIds, markLookup || {}),
    [canonicalMarkIds, markLookup],
  );

  // Reconcile saved directions with inference on cell switch. Keyed on the cell
  // as well as on `inferred`: moving between two courses with the SAME marks
  // (DBSC CC2 4D and 5D) leaves the mark input, and so `inferred`, unchanged, and
  // keying on `inferred` alone never re-applied the new course's saved sides. The
  // editor then showed its guess and onChange wrote the guess back (ra-m35p6).
  // Wait until inference is for the saved course's marks: on a switch to a course
  // with different marks, the first pass still holds the previous course's marks.
  useEffect(() => {
    const pending = savedDirectionsRef.current;
    if (!pending || inferred.length === 0) return;
    if (markInput !== pending.input) return;
    savedDirectionsRef.current = null;
    const saved = pending.dirs;
    if (!saved || saved.length === 0) return;
    const overrides = {};
    for (let i = 0; i < inferred.length; i++) {
      const inferredDir = inferred[i].rounding === 'ambiguous' ? 'port' : inferred[i].rounding;
      if (saved[i] && saved[i] !== inferredDir) {
        overrides[i] = saved[i];
      }
    }
    setManualOverrides(overrides);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inferred, markInput, directionCode, courseNumber, course?.id]);

  // Apply manual overrides on top of inferred roundings
  const roundings = useMemo(() => {
    return inferred.map((inf, i) => {
      if (manualOverrides[i]) {
        return { ...inf, rounding: manualOverrides[i], confident: true };
      }
      return inf;
    });
  }, [inferred, manualOverrides]);

  // Resolve effective start/finish for this course (3-level hierarchy)
  const effectiveLine = useMemo(() => {
    if (!resolvedCardLine) return null;
    return {
      startType: course?.startType ?? resolvedCardLine.startType,
      startOffsetNm: course?.startOffsetNm ?? resolvedCardLine.startOffsetNm,
      startOffsetAngle: course?.startOffsetAngle ?? resolvedCardLine.startOffsetAngle,
      startMarkA: course?.startMarkA ?? resolvedCardLine.startMarkA,
      startMarkB: course?.startMarkB ?? resolvedCardLine.startMarkB,
      finishType: course?.finishType ?? resolvedCardLine.finishType,
      finishOffsetNm: course?.finishOffsetNm ?? resolvedCardLine.finishOffsetNm,
      finishOffsetAngle: course?.finishOffsetAngle ?? resolvedCardLine.finishOffsetAngle,
      finishMarkA: course?.finishMarkA ?? resolvedCardLine.finishMarkA,
      finishMarkB: course?.finishMarkB ?? resolvedCardLine.finishMarkB,
    };
  }, [course?.startType, course?.startOffsetNm, course?.startOffsetAngle, course?.startMarkA, course?.startMarkB,
      course?.finishType, course?.finishOffsetNm, course?.finishOffsetAngle, course?.finishMarkA, course?.finishMarkB,
      resolvedCardLine]);

  // Calculate full distance including start/finish legs
  const distResult = useMemo(() => {
    if (!effectiveLine) return calculateDistance(canonicalMarkIds, markLookup || {});
    return calculateFullDistance(
      canonicalMarkIds,
      markLookup || {},
      { type: effectiveLine.startType, offsetNm: effectiveLine.startOffsetNm, markA: effectiveLine.startMarkA, markB: effectiveLine.startMarkB },
      { type: effectiveLine.finishType, offsetNm: effectiveLine.finishOffsetNm, markA: effectiveLine.finishMarkA, markB: effectiveLine.finishMarkB },
    );
  }, [canonicalMarkIds, markLookup, effectiveLine]);

  // Find missing marks (case-insensitive comparison)
  const markSet = useMemo(() => {
    const s = new Set();
    (marks || []).forEach((m) => s.add(m.id.toLowerCase()));
    return s;
  }, [marks]);

  const missingMarks = useMemo(
    () => [...new Set(markIds.filter((id) => !markSet.has(id.toLowerCase())))],
    [markIds, markSet],
  );

  // Build course object and call onChange (skip on cell switch to prevent flicker)
  useEffect(() => {
    const cellKey = `${directionCode}${courseNumber}`;
    if (prevCellRef.current !== cellKey) {
      prevCellRef.current = cellKey;
      return; // Cell just changed — wait for markInput to sync first
    }

    if (!onChange || canonicalMarkIds.length === 0) return;

    onChange({
      id: course?.id || stableId,
      directionCode: directionCode ?? null,
      courseNumber: courseNumber ?? 0,
      name: isNamedCourse ? (courseName || course?.name || 'Custom') : undefined,
      // Carry the named-course wind sector + start area through every onChange so
      // a later mark edit (which rebuilds the whole course object) can't wipe the
      // tags the user set via the dedicated controls. (ra-gbrd.19)
      windDirection: isNamedCourse ? (windDirection ?? course?.windDirection ?? null) : undefined,
      startArea: isNamedCourse ? (startArea ?? course?.startArea ?? null) : undefined,
      // ROOT CAUSE of ra-or1y "course blanked out": this previously forced
      // markIds.map((id) => id.toLowerCase()) on every onChange, persisting a
      // course whose markIds no longer resolved any mark → empty positions →
      // "No marks with coordinates" and an effectively blank course on reload.
      // Persist the canonical (real-case) ids — the same array used for inference
      // and distance above — so the stored course is internally consistent.
      markIds: canonicalMarkIds,
      markDirections: roundings.map((r) => r.rounding === 'ambiguous' ? 'port' : r.rounding),
      bearing: isNamedCourse ? 0 : Math.floor(DIRECTION_BEARINGS[directionCode] ?? 0),
      distance: distResult.totalNm ?? 0,
      displayName,
      startType: course?.startType,
      startMarkA: course?.startMarkA,
      startMarkB: course?.startMarkB,
      startOffsetNm: course?.startOffsetNm,
      startOffsetAngle: course?.startOffsetAngle,
      finishType: course?.finishType,
      finishMarkA: course?.finishMarkA,
      finishMarkB: course?.finishMarkB,
      finishOffsetNm: course?.finishOffsetNm,
      finishOffsetAngle: course?.finishOffsetAngle,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalMarkIds, roundings, directionCode, courseNumber, distResult.totalNm]);

  // Build a lowercase set of known mark IDs for auto-tokenizing
  const markIdsLower = useMemo(() => {
    const s = new Set();
    (marks || []).forEach((m) => s.add(m.id.toLowerCase()));
    return s;
  }, [marks]);
  const hasMultiCharMarks = useMemo(
    () => [...markIdsLower].some((id) => id.length > 1),
    [markIdsLower],
  );

  const handleMarkInputChange = useCallback((e) => {
    const raw = e.target.value;

    // Multi-char mode: any separator (space, comma, dash, slash, arrow, …) →
    // tokenise. Broadened per Chris (ra-npo7) so more input styles resolve to the
    // right mark names.
    if (hasMarkSeparator(raw)) {
      const tokens = splitMarkTokens(raw);
      const overrides = {};
      const ids = [];
      for (const token of tokens) {
        const parenMatch = token.match(/^(.+)\(([ps])\)$/i);
        if (parenMatch) {
          ids.push(parenMatch[1]);
          overrides[ids.length - 1] = parenMatch[2].toLowerCase() === 's' ? 'starboard' : 'port';
        } else {
          const last = token[token.length - 1];
          const stripped = token.slice(0, -1).toLowerCase();
          // Only treat trailing p/s as rounding if the remainder is a known mark
          if (token.length > 1 && (last === 'p' || last === 's') && markIdsLower.has(stripped)) {
            ids.push(stripped);
            overrides[ids.length - 1] = last === 's' ? 'starboard' : 'port';
          } else {
            ids.push(token);
          }
        }
      }
      // Preserve a trailing separator so the user can type a space to begin
      // the next mark token (otherwise every trailing space is stripped on
      // re-serialisation and the separator can never be entered).
      const trailing = endsWithMarkSeparator(raw) ? ' ' : '';
      setMarkInput(ids.map((id) => id.toUpperCase()).join(' ') + trailing);
      setManualOverrides(Object.keys(overrides).length > 0 ? overrides : {});
      return;
    }

    // Auto-tokenize: greedily match known multi-char mark IDs
    if (hasMultiCharMarks) {
      const lower = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
      const tokens = [];
      let pos = 0;
      let pending = ''; // accumulates unmatched chars

      while (pos < lower.length) {
        let bestMatch = null;
        // Try longest match first from current position
        for (let len = lower.length - pos; len >= 1; len--) {
          if (markIdsLower.has(lower.substring(pos, pos + len))) {
            bestMatch = lower.substring(pos, pos + len);
            break;
          }
        }
        if (bestMatch) {
          if (pending) { tokens.push(pending); pending = ''; }
          tokens.push(bestMatch);
          pos += bestMatch.length;
        } else {
          // No match — accumulate one char and keep scanning
          pending += lower[pos];
          pos++;
        }
      }
      if (pending) tokens.push(pending);

      setMarkInput(tokens.map((t) => t.toUpperCase()).join(' '));
      setManualOverrides({});
      return;
    }

    // Single-char mode (DBSC: A-Z marks)
    const overrides = {};
    const letters = [];
    for (let j = 0; j < raw.length; j++) {
      const ch = raw[j].toUpperCase();
      if (ch >= 'A' && ch <= 'Z') {
        const next = raw[j + 1];
        if (next === 'p') {
          overrides[letters.length] = 'port';
          letters.push(ch);
          j++;
          continue;
        }
        if (next === 's') {
          overrides[letters.length] = 'starboard';
          letters.push(ch);
          j++;
          continue;
        }
        letters.push(ch);
      }
    }
    setMarkInput(letters.join(''));
    setManualOverrides(Object.keys(overrides).length > 0 ? overrides : {});
  }, [hasMultiCharMarks, markIdsLower]);

  const toggleRounding = useCallback((index) => {
    setManualOverrides((prev) => {
      const current = roundings[index]?.rounding;
      const override = prev[index];
      const effectiveRounding = override || current;
      const next = effectiveRounding === 'port' ? 'starboard' : 'port';
      return { ...prev, [index]: next };
    });
  }, [roundings]);

  // Fuzzy suggestions for missing marks
  const getSuggestions = useCallback(
    (unknownId) => fuzzyMatchMark(unknownId, marks || []),
    [marks],
  );

  // Replace a mark ID in the input
  const replaceMark = useCallback((oldId, newId) => {
    setMarkInput((prev) => {
      // Replace first occurrence of the old mark (letter + optional p/s)
      const regex = new RegExp(oldId + '[ps]?');
      return prev.replace(regex, newId);
    });
  }, []);

  // Handle adding a new mark inline
  const handleAddNewMark = useCallback((markData) => {
    if (onAddMark) {
      onAddMark(markData);
    }
    setAddingMark(null);
  }, [onAddMark]);

  return (
    <div className="course-editor">
      <div className="course-editor-header">
        {isNamedCourse ? (
          <input
            type="text"
            className="course-editor-title-input"
            value={courseName || ''}
            onChange={(e) => onRenameCourse?.(e.target.value)}
            placeholder="Course name"
          />
        ) : (
          <h3 className="course-editor-title">{displayName}</h3>
        )}
        {isNamedCourse ? (
          <div className="course-editor-nav">
            {onRemoveCourse && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => onRemoveCourse(course?.id || stableId)}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className="course-editor-nav">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => prevNum != null && onNavigate?.(directionCode, prevNum)}
              disabled={prevNum == null}
              title={prevNum != null ? `${directionCode}${prevNum}` : undefined}
            >
              &larr; {directionCode}{prevNum ?? ''}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => nextNum != null && onNavigate?.(directionCode, nextNum)}
              disabled={nextNum == null}
              title={nextNum != null ? `${directionCode}${nextNum}` : undefined}
            >
              {directionCode}{nextNum ?? ''} &rarr;
            </button>
          </div>
        )}
      </div>

      {isNamedCourse && (
        <div className="course-named-tags-row">
          <label className="form-field course-wind-sector">
            <span className="form-label">
              Wind sector
              <span className="form-hint">groups the course by wind direction</span>
            </span>
            <select
              value={windDirection ?? course?.windDirection ?? ''}
              onChange={(e) => onWindDirectionChange?.(e.target.value || null)}
            >
              <option value="">— none —</option>
              {WIND_SECTORS.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field course-start-area">
            <span className="form-label">
              Start area
              <span className="form-hint">optional, e.g. harbour start</span>
            </span>
            <input
              type="text"
              value={startArea ?? course?.startArea ?? ''}
              onChange={(e) => onStartAreaChange?.(e.target.value)}
              placeholder="Start area"
            />
          </label>
        </div>
      )}

      <div className="course-sequence-row">
        <label className="form-field course-seq-input">
          <span className="form-label">Marks</span>
          <input
            type="text"
            value={markInput}
            onChange={handleMarkInputChange}
            placeholder={isNamedCourse ? 'MUG KSH BRO (space-separated)' : 'ADEFS'}
            spellCheck={false}
            autoComplete="off"
            className="mono-input"
          />
        </label>
        {roundings.length > 0 && (
          <div className="form-field course-code-display">
            <span className="form-label">Course code</span>
            <div className="course-code">
              {roundings.map((r, i) => {
                const actual = r.rounding === 'ambiguous' ? 'port' : r.rounding;
                return (
                  <span
                    key={`cc-${i}`}
                    className="course-code-mark"
                    onClick={() => toggleRounding(i)}
                    title={`${r.markId}: ${actual} — click to toggle`}
                  >
                    <span className="course-code-id">{r.markId.toUpperCase()}</span>
                    <span className={`course-code-dir ${actual}`}>
                      {actual === 'port' ? 'p' : 's'}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {children && React.cloneElement(children, { highlightedIndex: highlightedLeg, onClearHighlight: () => setHighlightedLeg(null) })}

      {/* Leg table: start, marks/legs, finish, total */}
      {canonicalMarkIds.length > 0 && (
        <LegTable
          markIds={canonicalMarkIds}
          roundings={roundings}
          distResult={distResult}
          effectiveLine={effectiveLine}
          resolvedCardLine={resolvedCardLine}
          course={course}
          adjacentCourse={adjacentCourse}
          onChange={onChange}
          onToggleRounding={toggleRounding}
          directionCode={directionCode}
          courseNumber={courseNumber}
          stableId={stableId}
          displayName={displayName}
          marks={marks}
          markLookup={markLookup}
          windBearing={isNamedCourse ? null : DIRECTION_BEARINGS[directionCode]}
          highlightedLeg={highlightedLeg}
          onLegClick={(i) => setHighlightedLeg(highlightedLeg === i ? null : i)}
        />
      )}

      {/* Missing mark warnings */}
      {missingMarks.length > 0 && (
        <div className="missing-marks">
          {missingMarks.map((id) => (
            <MissingMarkWarning
              key={id}
              markId={id}
              suggestions={getSuggestions(id)}
              isAdding={addingMark === id}
              onStartAdd={() => setAddingMark(id)}
              onCancelAdd={() => setAddingMark(null)}
              onReplace={(newId) => replaceMark(id, newId)}
              onAddNew={handleAddNewMark}
            />
          ))}
        </div>
      )}

      {/* Copy-from-neighbour CTA: only on a blank cell with a defined neighbour.
          Replaces the old automatic pre-population. (Chris, ra-npo7) */}
      {!course && markInput === '' && adjacentCourse?.markIds?.length > 0 && (
        <button
          type="button"
          className="btn btn-xs variation-copy-btn"
          onClick={copyFromNeighbour}
          title="Copy this cell's marks from the neighbouring course"
        >
          Copy marks from {directionCode}{prevNumber ?? courseNumber - 1}
        </button>
      )}
    </div>
  );
}

/**
 * Compute signed True Wind Angle for a leg bearing relative to wind direction.
 * Positive = starboard tack, negative = port tack.
 * Range: -180 (dead downwind port) to +180 (dead downwind stbd).
 * 0 = head-to-wind.
 */
function computeTWA(legBearing, windBearing) {
  // TWA = how far the boat heading is from head-to-wind
  // Head-to-wind bearing = windBearing (wind blows FROM this direction)
  let diff = legBearing - windBearing;
  diff = ((diff % 360) + 540) % 360 - 180; // normalize to -180..+180
  return diff;
}

/** Point-of-sail label from TWA. */
function pointOfSail(twa) {
  const abs = Math.abs(twa);
  if (abs <= 30) return 'beat';
  if (abs <= 60) return 'close hauled';
  if (abs <= 80) return 'close reach';
  if (abs <= 100) return 'beam reach';
  if (abs <= 150) return 'broad reach';
  return 'run';
}

/** Format signed angle with explicit sign. */
function signedAngle(a) {
  return `${a > 0 ? '+' : ''}${Math.round(a)}°`;
}

/**
 * Adjust turn angle to match the rounding direction.
 * computeTurnAngle normalizes to (-180, +180] (shortest path),
 * but when we know the rounding direction, the boat may take the longer path.
 * Port = counter-clockwise (negative), starboard = clockwise (positive).
 */
function adjustTurnAngle(angle, rounding) {
  if (angle == null || !rounding) return angle;
  // Port expects negative, starboard expects positive
  const wantsNeg = rounding === 'port';
  if (wantsNeg && angle > 0) return angle - 360;  // e.g. +170 → -190
  if (!wantsNeg && angle < 0) return angle + 360;  // e.g. -170 → +190
  return angle;
}

/**
 * Resolve the leg bearing for a non-fixed start/finish line, for the leg-table
 * Brg/TWA text. Derived from the canonical geo.offsetLineBearing (the mark→line
 * placement bearing the chart draws with) so keyword and numeric inputs stay
 * consistent — a downwind/upwind→bearing switch is display-neutral. (ra-etwi)
 *
 * offsetAngle: 'downwind' | 'upwind' | numeric bearing string/number
 * windBearing: compass bearing wind blows FROM
 * isStart: true = start line, false = finish.
 * Returns the leg's compass bearing: FROM the line TO the first mark (start) or
 *   FROM the last mark TO the line (finish). The start leg runs opposite the
 *   placement (line→mark), the finish leg runs along it (mark→line).
 */
function resolveOffsetBearing(offsetAngle, windBearing, isStart) {
  const placement = offsetLineBearing(offsetAngle, windBearing, isStart);
  if (placement == null) return null;
  return isStart ? (placement + 180) % 360 : placement;
}

/** Unified leg table showing start, mark roundings, leg distances, and finish. */
function LegTable({
  markIds, roundings, distResult, effectiveLine, resolvedCardLine,
  course, adjacentCourse, onChange, onToggleRounding,
  directionCode, courseNumber, stableId, displayName,
  marks, markLookup, windBearing,
  highlightedLeg, onLegClick,
}) {
  function updateCourseLineField(updates) {
    if (!onChange) return;
    onChange({
      id: course?.id || stableId,
      directionCode,
      courseNumber,
      markIds: course?.markIds || markIds,
      markDirections: course?.markDirections || roundings.map((r) => r.rounding === 'ambiguous' ? 'port' : r.rounding),
      bearing: course?.bearing ?? 0,
      distance: distResult?.totalNm ?? 0,
      displayName,
      startType: course?.startType,
      startMarkA: course?.startMarkA,
      startMarkB: course?.startMarkB,
      startOffsetNm: course?.startOffsetNm,
      startOffsetAngle: course?.startOffsetAngle,
      finishType: course?.finishType,
      finishMarkA: course?.finishMarkA,
      finishMarkB: course?.finishMarkB,
      finishOffsetNm: course?.finishOffsetNm,
      finishOffsetAngle: course?.finishOffsetAngle,
      ...updates,
    });
  }

  const startDist = distResult.startLegNm;
  const finishDist = distResult.finishLegNm;
  const hasLines = !!effectiveLine || !!resolvedCardLine;
  const lookup = markLookup || {};

  // Mark name lookup
  const markNameMap = useMemo(() => {
    const m = {};
    for (const mk of (marks || [])) m[mk.id] = mk.name || mk.id;
    return m;
  }, [marks]);

  // Compute leg bearing + signed TWA
  function getLegInfo(fromId, toId) {
    const posA = lookup[fromId];
    const posB = lookup[toId];
    if (!posA || !posB) return { bearing: null, twa: null };
    const brg = bearingDeg(posA[0], posA[1], posB[0], posB[1]);
    const twa = windBearing != null ? computeTWA(brg, windBearing) : null;
    return { bearing: Math.round(brg), twa };
  }

  // Compute turn angles for first/last marks using start/finish line midpoints
  const endTurnAngles = useMemo(() => {
    const result = { first: null, last: null };
    if (markIds.length < 2) return result;
    const line = effectiveLine || resolvedCardLine;
    if (!line) return result;

    // Start line midpoint → first mark bearing
    const startType = course?.startType ?? line?.startType;
    if (startType === 'fixed') {
      const mA = lookup[course?.startMarkA ?? line?.startMarkA];
      const mB = lookup[course?.startMarkB ?? line?.startMarkB];
      if (mA && mB) {
        const mid = [(mA[0] + mB[0]) / 2, (mA[1] + mB[1]) / 2];
        const firstPos = lookup[markIds[0]];
        const secondPos = lookup[markIds[1]];
        if (firstPos && secondPos) {
          const inBrg = bearingDeg(mid[0], mid[1], firstPos[0], firstPos[1]);
          const outBrg = bearingDeg(firstPos[0], firstPos[1], secondPos[0], secondPos[1]);
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.first = a;
        }
      }
    } else if (startType === 'fixed_mark') {
      // Fixed-mark start: line sits on markA; the approach axis is a numeric
      // offsetAngle when set, else the course's departure direction. (ra-gbrd.22)
      const markAId = course?.startMarkA ?? line?.startMarkA;
      const numeric = Number(course?.startOffsetAngle ?? line?.startOffsetAngle);
      const approachBrg = Number.isFinite(numeric)
        ? ((numeric % 360) + 360) % 360
        : fixedMarkLineBearing(markIds, lookup, markAId, true);
      if (approachBrg != null) {
        const firstPos = lookup[markIds[0]];
        const secondPos = lookup[markIds[1]];
        if (firstPos && secondPos) {
          const inBrg = approachBrg;
          const outBrg = bearingDeg(firstPos[0], firstPos[1], secondPos[0], secondPos[1]);
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.first = a;
        }
      }
    } else {
      // Vessel/offset start: use resolved offset angle
      const startAngle = course?.startOffsetAngle ?? line?.startOffsetAngle;
      const approachBrg = resolveOffsetBearing(startAngle, windBearing, true);
      if (approachBrg != null) {
        const firstPos = lookup[markIds[0]];
        const secondPos = lookup[markIds[1]];
        if (firstPos && secondPos) {
          const inBrg = approachBrg; // direction the boat is heading toward first mark
          const outBrg = bearingDeg(firstPos[0], firstPos[1], secondPos[0], secondPos[1]);
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.first = a;
        }
      }
    }

    // Last mark → finish line midpoint bearing
    const finishType = course?.finishType ?? line?.finishType;
    const lastIdx = markIds.length - 1;
    if (finishType === 'fixed') {
      const mA = lookup[course?.finishMarkA ?? line?.finishMarkA];
      const mB = lookup[course?.finishMarkB ?? line?.finishMarkB];
      if (mA && mB) {
        const mid = [(mA[0] + mB[0]) / 2, (mA[1] + mB[1]) / 2];
        const lastPos = lookup[markIds[lastIdx]];
        const prevPos = lookup[markIds[lastIdx - 1]];
        if (lastPos && prevPos) {
          const inBrg = bearingDeg(prevPos[0], prevPos[1], lastPos[0], lastPos[1]);
          const outBrg = bearingDeg(lastPos[0], lastPos[1], mid[0], mid[1]);
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.last = a;
        }
      }
    } else if (finishType === 'fixed_mark') {
      // Fixed-mark finish: line sits on markA; the departure axis is a numeric
      // offsetAngle when set, else the course's final-approach direction.
      // (ra-gbrd.22)
      const markAId = course?.finishMarkA ?? line?.finishMarkA;
      const numeric = Number(course?.finishOffsetAngle ?? line?.finishOffsetAngle);
      const departureBrg = Number.isFinite(numeric)
        ? ((numeric % 360) + 360) % 360
        : fixedMarkLineBearing(markIds, lookup, markAId, false);
      if (departureBrg != null) {
        const lastPos = lookup[markIds[lastIdx]];
        const prevPos = lookup[markIds[lastIdx - 1]];
        if (lastPos && prevPos) {
          const inBrg = bearingDeg(prevPos[0], prevPos[1], lastPos[0], lastPos[1]);
          const outBrg = departureBrg;
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.last = a;
        }
      }
    } else {
      // Vessel/offset finish: use resolved offset angle
      const finishAngle = course?.finishOffsetAngle ?? line?.finishOffsetAngle;
      const departureBrg = resolveOffsetBearing(finishAngle, windBearing, false);
      if (departureBrg != null) {
        const lastPos = lookup[markIds[lastIdx]];
        const prevPos = lookup[markIds[lastIdx - 1]];
        if (lastPos && prevPos) {
          const inBrg = bearingDeg(prevPos[0], prevPos[1], lastPos[0], lastPos[1]);
          const outBrg = departureBrg;
          let a = (outBrg - inBrg) % 360;
          if (a > 180) a -= 360;
          if (a <= -180) a += 360;
          result.last = a;
        }
      }
    }

    return result;
  }, [markIds, course, effectiveLine, resolvedCardLine, lookup, windBearing]);

  // Build map of adjusted turn angles per mark ID within this course (for duplicate-mark warning)
  const turnAnglesByMark = useMemo(() => {
    const map = {};
    for (const r of roundings) {
      if (r.turnAngle == null) continue;
      const dir = r.rounding === 'ambiguous' ? 'port' : r.rounding;
      const adj = adjustTurnAngle(r.turnAngle, dir);
      if (!map[r.markId]) map[r.markId] = [];
      map[r.markId].push(adj);
    }
    return map;
  }, [roundings]);

  // Build map of turn angles from adjacent course for cross-course comparison
  const adjTurnAngles = useMemo(() => {
    if (!adjacentCourse?.markIds || !adjacentCourse.markIds.length) return {};
    const adjLookup = markLookup || {};
    const map = {};
    const ids = adjacentCourse.markIds;
    for (let i = 1; i < ids.length - 1; i++) {
      const posA = adjLookup[ids[i - 1]];
      const posB = adjLookup[ids[i]];
      const posC = adjLookup[ids[i + 1]];
      if (!posA || !posB || !posC) continue;
      const inBrg = bearingDeg(posA[0], posA[1], posB[0], posB[1]);
      const outBrg = bearingDeg(posB[0], posB[1], posC[0], posC[1]);
      let angle = (outBrg - inBrg) % 360;
      if (angle > 180) angle -= 360;
      if (angle <= -180) angle += 360;
      if (!map[ids[i]]) map[ids[i]] = [];
      map[ids[i]].push(angle);
    }
    return map;
  }, [adjacentCourse, markLookup]);

  // Collect warnings for a mark
  function markWarnings(r, i) {
    const warnings = [];
    const isFirst = i === 0;
    const isLast = i === markIds.length - 1;
    const actual = r ? (r.rounding === 'ambiguous' ? 'port' : r.rounding) : 'port';

    // Check for >200° effective turn (likely a passing mark)
    const rawAngle = isFirst ? endTurnAngles.first : isLast ? endTurnAngles.last : r?.turnAngle;
    if (rawAngle != null) {
      const adj = adjustTurnAngle(rawAngle, actual);
      if (Math.abs(adj) > 200) {
        warnings.push('Passing mark? Effective turn is > 200°');
      }
    }

    if (isFirst || isLast) return warnings;

    const predicted = r?.rounding;

    // 1. Rounding doesn't match turn angle sign
    if (predicted && predicted !== 'ambiguous' && predicted !== actual) {
      warnings.push(`Geometry predicts ${predicted} but set to ${actual}`);
    }

    // 2. Same mark rounded in opposite directions within this course
    const sameMark = turnAnglesByMark[r?.markId];
    if (sameMark && sameMark.length > 1) {
      const hasPort = sameMark.some((a) => a < 0);
      const hasStb = sameMark.some((a) => a > 0);
      if (hasPort && hasStb) {
        warnings.push(`${r.markId} is rounded in opposite directions in this course`);
      }
    }

    // 3. Same mark has opposite turn direction in adjacent course (sign flip)
    if (r?.turnAngle != null && adjTurnAngles[r.markId]) {
      for (const adjAngle of adjTurnAngles[r.markId]) {
        if ((r.turnAngle > 0 && adjAngle < 0) || (r.turnAngle < 0 && adjAngle > 0)) {
          const adjName = `${directionCode}${courseNumber - 1}`;
          const adjDir = adjAngle > 0 ? 'starboard' : 'port';
          const thisDir = r.turnAngle > 0 ? 'starboard' : 'port';
          warnings.push(`${r.markId} rounds ${thisDir} here but ${adjDir} in ${adjName}`);
          break;
        }
      }
    }

    return warnings;
  }

  return (
    <div className="leg-table-wrap">
      <table className="leg-table">
        <thead>
          <tr>
            <th>Mark</th>
            <th></th>
            <th className="lt-rounding">Rounding</th>
            <th className="lt-angle">Angle</th>
            <th className="lt-dist">Dist</th>
          </tr>
        </thead>
        <tbody>
          {/* Start mark row */}
          {hasLines && (
            <tr className="lt-mark-row lt-line-row lt-start-row">
              <td className="lt-mark-id">Start</td>
              <td className="lt-desc">
                <LineEditor
                  lineKey="start"
                  course={course}
                  resolvedCardLine={resolvedCardLine}
                  effectiveLine={effectiveLine}
                  updateCourseLineField={updateCourseLineField}
                />
              </td>
              <td className="lt-rounding-cell">between</td>
              <td></td>
              <td></td>
            </tr>
          )}

          {/* Start → first mark leg */}
          {hasLines && markIds.length > 0 && (() => {
            const startLegInfo = (() => {
              const line = effectiveLine || resolvedCardLine;
              const type = course?.startType ?? line?.startType;
              const firstPos = lookup[markIds[0]];
              if (!firstPos) return {};
              if (type === 'fixed') {
                const mA = lookup[course?.startMarkA ?? line?.startMarkA];
                const mB = lookup[course?.startMarkB ?? line?.startMarkB];
                if (mA && mB) {
                  const mid = [(mA[0] + mB[0]) / 2, (mA[1] + mB[1]) / 2];
                  const brg = bearingDeg(mid[0], mid[1], firstPos[0], firstPos[1]);
                  const twa = windBearing != null ? computeTWA(brg, windBearing) : null;
                  return { bearing: Math.round(brg), twa };
                }
              } else {
                // Vessel/offset: bearing from start line to first mark = approach direction
                const startAngle = course?.startOffsetAngle ?? line?.startOffsetAngle;
                const brg = resolveOffsetBearing(startAngle, windBearing, true);
                if (brg != null) {
                  const twa = computeTWA(brg, windBearing);
                  return { bearing: Math.round(brg), twa };
                }
              }
              return {};
            })();
            return (
              <tr className="lt-leg-row">
                <td className="lt-leg-label">Start &rarr; {markIds[0].toUpperCase()}</td>
                <td className="lt-desc lt-leg-pos">
                  {startLegInfo.twa != null && `${pointOfSail(startLegInfo.twa)} TWA ${signedAngle(startLegInfo.twa)}`}
                </td>
                <td></td>
                <td className="lt-angle-cell">
                  {startLegInfo.bearing != null && `Brg ${startLegInfo.bearing}°`}
                </td>
                <td className="lt-dist-cell">{startDist != null ? startDist.toFixed(2) : ''}</td>
              </tr>
            );
          })()}

          {/* Course marks interleaved with mark-to-mark legs */}
          {markIds.map((id, i) => {
            const r = roundings[i];
            const isFirst = i === 0;
            const isLast = i === markIds.length - 1;

            // Mark row — use endTurnAngles for first/last marks
            const rawTurnAngle = isFirst ? endTurnAngles.first : isLast ? endTurnAngles.last : r?.turnAngle;
            const actual = r ? (r.rounding === 'ambiguous' ? 'port' : r.rounding) : 'port';
            const turnAngle = adjustTurnAngle(rawTurnAngle, actual);
            const warns = markWarnings(r, i);

            const isHighlighted = highlightedLeg === i;
            const markRow = (
              <tr
                key={`mark-${i}`}
                className={`lt-mark-row lt-clickable ${r?.confident === false ? 'lt-uncertain' : ''} ${isHighlighted ? 'lt-highlighted' : ''}`}
                onClick={() => onLegClick?.(i)}
              >
                <td className="lt-mark-id">{id.toUpperCase()}</td>
                <td className="lt-desc lt-mark-name">
                  {markNameMap[id] || ''}
                  {warns.length > 0 && (
                    <span className="lt-warn-text">{warns.join('; ')}</span>
                  )}
                </td>
                <td className="lt-rounding-cell">
                  <button
                    type="button"
                    className={`rounding-toggle ${actual}`}
                    onClick={() => onToggleRounding(i)}
                    title="Click to toggle port/starboard"
                  >
                    {actual === 'port' ? 'P' : 'S'}
                  </button>
                </td>
                <td className="lt-angle-cell">
                  {turnAngle != null
                    ? `Turn ${signedAngle(turnAngle)}`
                    : ''}
                </td>
                <td></td>
              </tr>
            );

            // Leg row after this mark
            const leg = distResult.legs?.[i];
            const legInfo = !isLast && markIds[i + 1] ? getLegInfo(id, markIds[i + 1]) : {};
            const legRow = !isLast && leg ? (
              <tr key={`leg-${i}`} className="lt-leg-row">
                <td className="lt-leg-label">{(leg.from || '').toUpperCase()} &rarr; {(leg.to || '').toUpperCase()}</td>
                <td className="lt-desc lt-leg-pos">
                  {legInfo.twa != null && `${pointOfSail(legInfo.twa)} TWA ${signedAngle(legInfo.twa)}`}
                </td>
                <td></td>
                <td className="lt-angle-cell">
                  {legInfo.bearing != null && `Brg ${legInfo.bearing}°`}
                </td>
                <td className="lt-dist-cell">{leg.distNm != null ? leg.distNm.toFixed(2) : '?'}</td>
              </tr>
            ) : null;

            return <Fragment key={`group-${i}`}>{markRow}{legRow}</Fragment>;
          })}

          {/* Last mark → Finish leg */}
          {hasLines && markIds.length > 0 && (() => {
            const finishLegInfo = (() => {
              const line = effectiveLine || resolvedCardLine;
              const type = course?.finishType ?? line?.finishType;
              const lastPos = lookup[markIds[markIds.length - 1]];
              if (!lastPos) return {};
              if (type === 'fixed') {
                const mA = lookup[course?.finishMarkA ?? line?.finishMarkA];
                const mB = lookup[course?.finishMarkB ?? line?.finishMarkB];
                if (mA && mB) {
                  const mid = [(mA[0] + mB[0]) / 2, (mA[1] + mB[1]) / 2];
                  const brg = bearingDeg(lastPos[0], lastPos[1], mid[0], mid[1]);
                  const twa = windBearing != null ? computeTWA(brg, windBearing) : null;
                  return { bearing: Math.round(brg), twa };
                }
              } else {
                // Vessel/offset: bearing from last mark to finish line = departure direction
                const finishAngle = course?.finishOffsetAngle ?? line?.finishOffsetAngle;
                const brg = resolveOffsetBearing(finishAngle, windBearing, false);
                if (brg != null) {
                  const twa = computeTWA(brg, windBearing);
                  return { bearing: Math.round(brg), twa };
                }
              }
              return {};
            })();
            return (
              <tr className="lt-leg-row">
                <td className="lt-leg-label">{markIds[markIds.length - 1].toUpperCase()} &rarr; Finish</td>
                <td className="lt-desc lt-leg-pos">
                  {finishLegInfo.twa != null && `${pointOfSail(finishLegInfo.twa)} TWA ${signedAngle(finishLegInfo.twa)}`}
                </td>
                <td></td>
                <td className="lt-angle-cell">
                  {finishLegInfo.bearing != null && `Brg ${finishLegInfo.bearing}°`}
                </td>
                <td className="lt-dist-cell">{finishDist != null ? finishDist.toFixed(2) : ''}</td>
              </tr>
            );
          })()}

          {/* Finish mark row */}
          {hasLines && (
            <tr className="lt-mark-row lt-line-row lt-finish-row">
              <td className="lt-mark-id">Finish</td>
              <td className="lt-desc">
                <LineEditor
                  lineKey="finish"
                  course={course}
                  resolvedCardLine={resolvedCardLine}
                  effectiveLine={effectiveLine}
                  updateCourseLineField={updateCourseLineField}
                />
              </td>
              <td className="lt-rounding-cell">between</td>
              <td></td>
              <td></td>
            </tr>
          )}

          {/* Total */}
          <tr className="lt-total-row">
            <td></td>
            <td></td>
            <td></td>
            <td className="lt-total-label">Total</td>
            <td className="lt-dist-cell lt-total-val">
              {distResult.totalNm != null ? `${distResult.totalNm.toFixed(2)} nm` : '?'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Inline start/finish line editor within leg table row. */
function LineEditor({ lineKey, course, resolvedCardLine, effectiveLine, updateCourseLineField }) {
  const typeField = `${lineKey}Type`;
  const markAField = `${lineKey}MarkA`;
  const markBField = `${lineKey}MarkB`;
  const offsetField = `${lineKey}OffsetNm`;
  const angleField = `${lineKey}OffsetAngle`;
  const defaultAngle = lineKey === 'start' ? 'downwind' : 'upwind';
  const isInherited = course?.[typeField] == null;
  const el = effectiveLine || resolvedCardLine;
  const label = lineKey === 'start' ? 'Start' : 'Finish';

  if (!el) return <span>{label}</span>;

  if (isInherited) {
    const type = el[typeField];
    return (
      <span className="lt-line-inherited">
        <strong>{label}:</strong>{' '}
        {type === 'fixed'
          ? `${el[markAField] || '?'} \u2194 ${el[markBField] || '?'}`
          : `${el[offsetField] ?? '?'} nm ${el[angleField] || defaultAngle}`}
        <span className="inherited-badge">inherited</span>
        <button
          type="button"
          className="btn btn-xs"
          onClick={() => updateCourseLineField({
            [typeField]: el[typeField],
            [markAField]: el[markAField] || '',
            [markBField]: el[markBField] || '',
            [offsetField]: el[offsetField],
            [angleField]: el[angleField] || defaultAngle,
          })}
        >
          Override
        </button>
      </span>
    );
  }

  const type = course[typeField] === 'offset' ? 'vessel' : course[typeField];
  return (
    <span className="lt-line-edit">
      <strong>{label}:</strong>{' '}
      <select value={type} onChange={(e) => updateCourseLineField({ [typeField]: e.target.value })}>
        <option value="fixed">Fixed</option>
        <option value="fixed_mark">Fixed mark</option>
        <option value="vessel">Vessel</option>
      </select>
      {course[typeField] === 'fixed' ? (
        <>
          <input
            type="text"
            value={course[markAField] || ''}
            onChange={(e) => updateCourseLineField({ [markAField]: e.target.value })}
            placeholder="Mark A"
            className="inline-input mark-input"
          />
          <span>&harr;</span>
          <input
            type="text"
            value={course[markBField] || ''}
            onChange={(e) => updateCourseLineField({ [markBField]: e.target.value })}
            placeholder="Mark B"
            className="inline-input mark-input"
          />
        </>
      ) : course[typeField] === 'fixed_mark' ? (
        <>
          {/* Line sits ON a single fixed mark, oriented by a bearing
              (auto = course approach/departure, or a custom value). (ra-gbrd.22) */}
          <input
            type="text"
            value={course[markAField] || ''}
            onChange={(e) => updateCourseLineField({ [markAField]: e.target.value })}
            placeholder="Mark"
            className="inline-input mark-input"
          />
          <input
            type="number"
            value={course[offsetField] ?? 0}
            onChange={(e) => updateCourseLineField({ [offsetField]: parseFloat(e.target.value) || 0 })}
            step="0.1"
            min="0"
            className="inline-input mark-input"
            title="Offset of the line from the mark (0 = on the mark)"
          />
          <span>nm</span>
          {(() => {
            const angle = course[angleField] ?? el[angleField];
            const isAuto = angle == null || angle === '' || angle === 'auto'
              || (typeof angle === 'string' && !Number.isFinite(Number(angle)));
            return (
              <>
                <select
                  value={isAuto ? 'auto' : 'custom'}
                  onChange={(e) => {
                    updateCourseLineField({ [angleField]: e.target.value === 'auto' ? 'auto' : '0' });
                  }}
                  className="inline-input"
                >
                  <option value="auto">auto</option>
                  <option value="custom">bearing...</option>
                </select>
                {!isAuto && (
                  <input
                    type="number"
                    value={angle}
                    onChange={(e) => updateCourseLineField({ [angleField]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const cur = Number(angle);
                        const base = Number.isFinite(cur) ? cur : 0;
                        const next = (((base + (e.key === 'ArrowUp' ? 1 : -1)) % 360) + 360) % 360;
                        updateCourseLineField({ [angleField]: String(next) });
                      }
                    }}
                    step="1"
                    className="inline-input mark-input"
                    title="Line orientation bearing (axis the line crosses; loops 0–359)"
                    style={{ width: '4em' }}
                  />
                )}
              </>
            );
          })()}
        </>
      ) : (
        <>
          <input
            type="number"
            value={course[offsetField] ?? el[offsetField]}
            onChange={(e) => updateCourseLineField({ [offsetField]: parseFloat(e.target.value) || 0 })}
            step="0.1"
            min="0"
            className="inline-input mark-input"
            title="Distance from mark to vessel"
          />
          <span>nm</span>
          {(() => {
            const angle = course[angleField] ?? el[angleField] ?? defaultAngle;
            const isCustom = angle !== 'upwind' && angle !== 'downwind';
            const isStart = lineKey === 'start';
            // Wind the chart renders with (same source as CoursePreview's
            // `bearing`), so a keyword→bearing switch is chart-neutral.
            const windBearing = course?.bearing;
            const setBearing = (deg) => {
              const wrapped = (((Math.round(deg) % 360) + 360) % 360);
              updateCourseLineField({ [angleField]: String(wrapped) });
            };
            return (
              <>
                <select
                  value={isCustom ? 'custom' : angle}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== 'custom') {
                      updateCourseLineField({ [angleField]: v });
                      return;
                    }
                    // Default the bearing input to the ABSOLUTE bearing the
                    // current downwind/upwind keyword already resolves to, so
                    // switching to a custom bearing does not move the chart.
                    const resolved = offsetLineBearing(angle, windBearing, isStart);
                    setBearing(Number.isFinite(resolved) ? resolved : 0);
                  }}
                  className="inline-input"
                >
                  <option value="downwind">downwind</option>
                  <option value="upwind">upwind</option>
                  <option value="custom">bearing...</option>
                </select>
                {isCustom && (
                  <input
                    type="number"
                    value={angle}
                    onChange={(e) => updateCourseLineField({ [angleField]: e.target.value })}
                    onKeyDown={(e) => {
                      // Arrow keys step the bearing and LOOP (0 ↓ → 359, 359 ↑ → 0),
                      // updating the chart immediately. (ra-etwi)
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const cur = Number(angle);
                        const base = Number.isFinite(cur) ? cur : 0;
                        setBearing(base + (e.key === 'ArrowUp' ? 1 : -1));
                      }
                    }}
                    step="1"
                    className="inline-input mark-input"
                    title="Absolute bearing from mark to vessel (loops 0–359)"
                    style={{ width: '4em' }}
                  />
                )}
              </>
            );
          })()}
        </>
      )}
      <button
        type="button"
        className="btn btn-xs"
        onClick={() => updateCourseLineField({ [typeField]: null, [markAField]: null, [markBField]: null, [offsetField]: null, [angleField]: null })}
        title="Inherit from card"
      >
        &times;
      </button>
    </span>
  );
}

/**
 * Inline warning + resolution for a missing mark.
 */
function MissingMarkWarning({
  markId,
  suggestions,
  isAdding,
  onStartAdd,
  onCancelAdd,
  onReplace,
  onAddNew,
}) {
  const [name, setName] = useState(markId);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [appearance, setAppearance] = useState('');

  function handleSubmitNew(e) {
    e.preventDefault();
    onAddNew({
      id: markId,
      name: name || markId,
      latitude: parseFloat(lat) || 0,
      longitude: parseFloat(lng) || 0,
      appearance: appearance || '',
    });
  }

  return (
    <div className="missing-mark-warning">
      <div className="missing-mark-header">
        <span className="warning-icon">&#9888;</span>
        <span>Mark &ldquo;{markId}&rdquo; not found</span>
        <button type="button" className="btn btn-xs" onClick={onStartAdd}>
          + Add
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="mark-suggestions">
          <span className="form-hint">Did you mean:</span>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="btn btn-xs btn-suggestion"
              onClick={() => onReplace(s.id)}
            >
              {s.id} ({s.name})
            </button>
          ))}
        </div>
      )}

      {isAdding && (
        <form className="add-mark-form" onSubmit={handleSubmitNew}>
          <div className="form-row">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="input-sm"
            />
            <input
              type="number"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="Lat"
              step="any"
              className="input-sm"
            />
            <input
              type="number"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="Lng"
              step="any"
              className="input-sm"
            />
            <input
              type="text"
              value={appearance}
              onChange={(e) => setAppearance(e.target.value)}
              placeholder="Appearance"
              className="input-sm"
            />
          </div>
          <div className="form-row">
            <button type="submit" className="btn btn-xs btn-primary">Save mark</button>
            <button type="button" className="btn btn-xs" onClick={onCancelAdd}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
